import { formatCooldownClock } from '@/components/chat/utils/chatCooldown';
import { Button } from '@heidi/design-system/button';
import { TypographyP } from '@heidi/design-system/typography';
import { useEffect, useState, type JSX } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

interface RateLimitErrorBlockProps {
  onRetry?: () => void;
  /** Epoch ms until Try again and send are allowed. Null once the wait has ended. */
  cooldownUntil?: number | null;
}

export const RateLimitErrorBlock = ({
  onRetry,
  cooldownUntil = null,
}: RateLimitErrorBlockProps): JSX.Element => {
  const intl = useIntl();
  const [now, setNow] = useState(() => Date.now());

  const remainingMs = cooldownUntil !== null ? cooldownUntil - now : 0;
  const coolingDown = cooldownUntil !== null && remainingMs > 0;

  useEffect(() => {
    if (!coolingDown) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [coolingDown]);

  return (
    <div className="flex w-full flex-row items-center gap-3 rounded-lg border border-validation-destructive-border bg-validation-destructive-muted p-4">
      <TypographyP className="min-w-0 flex-1 text-sm text-validation-destructive">
        {coolingDown ? (
          <FormattedMessage
            id="chat.rateLimitError.countdown"
            defaultMessage="Your rate limit has been exceeded. Try again in {duration}."
            description="Evidence rate-limit error. {duration} is a live m:ss countdown."
            values={{ duration: formatCooldownClock(remainingMs) }}
          />
        ) : (
          <FormattedMessage
            id="chat.rateLimitError.ready"
            defaultMessage="Your rate limit has been exceeded."
            description="Evidence rate-limit error after the wait has elapsed."
          />
        )}
      </TypographyP>
      {onRetry && (
        <Button
          variant="outline"
          size="md"
          disabled={coolingDown}
          onClick={() => void onRetry()}
          className="shrink-0 transition-colors"
        >
          {intl.formatMessage({
            id: 'chat.rateLimitError.tryAgain',
            defaultMessage: 'Try again',
            description: 'Button that resends the last Evidence message after a rate limit',
          })}
        </Button>
      )}
    </div>
  );
};
